import { createApi } from "@reduxjs/toolkit/query/react";

import type {
  ICreatePositionRequest,
  IUpdatePositionRequest,
  TCreatePositionResponse,
  TGetAllPositionsDataRequest,
  TGetAllPositionsDataResponse,
  TGetPositionByIdRequest,
  TGetPositionByIdResponse,
  TUpdatePositionResponse,
} from "@/types/(company)/positions.interface";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";

export const companyPositionsApi = createApi({
  reducerPath: "companyPositionsApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["getAllPositions", "getPositionById"],
  endpoints: (builder) => ({
    getAllPositions: builder.query<TGetAllPositionsDataResponse, TGetAllPositionsDataRequest>({
      query: ({ page, limit, search }) => ({
        url: "/company/positions",
        method: "GET",
        params: {
          page,
          limit,
          ...(search ? { search } : {}),
        },
      }),
      providesTags: ["getAllPositions"],
    }),
    getPositions: builder.query<TGetAllPositionsDataResponse, void>({
      query: () => ({
        url: "/company/positions",
        method: "GET",
        params: { page: 1, limit: 200 },
      }),
      providesTags: ["getAllPositions"],
    }),
    getPositionById: builder.query<TGetPositionByIdResponse, TGetPositionByIdRequest>({
      query: ({ id }) => ({
        url: `/company/positions/${id}`,
        method: "GET",
      }),
      providesTags: (_result, _err, { id }) => [{ type: "getPositionById", id }],
    }),
    createPosition: builder.mutation<TCreatePositionResponse, ICreatePositionRequest>({
      query: (data) => ({
        url: "/company/positions",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["getAllPositions"],
    }),
    updatePosition: builder.mutation<TUpdatePositionResponse, IUpdatePositionRequest>({
      query: ({ id, ...body }) => ({
        url: `/company/positions/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _err, { id }) => [
        "getAllPositions",
        { type: "getPositionById", id },
      ],
    }),
    deletePosition: builder.mutation<
      { success: boolean; message: string },
      number
    >({
      query: (id) => ({
        url: `/company/positions/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["getAllPositions"],
    }),
  }),
});

export const {
  useCreatePositionMutation,
  useGetAllPositionsQuery,
  useGetPositionsQuery,
  useGetPositionByIdQuery,
  useUpdatePositionMutation,
  useDeletePositionMutation,
} = companyPositionsApi;
